import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => {
    return articles.find((a) => a.slug === slug);
  }, [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles.filter((a) => a.id !== article.id).slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="pt-20">
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <i className="ri-article-line text-foreground-400 text-2xl"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-foreground-600 mb-6">抱歉，您要查看的文章不存在或已被删除。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOMeta
        title={`${article.title} - 芭乐视频资讯`}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',芭乐视频'}
        canonicalUrl={`https://balevideo.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogUrl={`https://balevideo.com/article/${article.slug}`}
      />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://balevideo.com/' },
          { '@type': 'ListItem', position: 2, name: '资讯', item: 'https://balevideo.com/article/why-bale-video-is-best-alternative' },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      }} />

      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        datePublished: article.date,
        author: { '@type': 'Person', name: article.author },
        publisher: {
          '@type': 'Organization',
          name: '芭乐视频',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://balevideo.com/article/${article.slug}`,
        },
      }} />

      <div className="pt-20">
        <Breadcrumb items={[
          { label: '首页', path: '/' },
          { label: '资讯', path: '/article/why-bale-video-is-best-alternative' },
          { label: article.title },
        ]} />

        <article className="w-full px-4 md:px-6 py-6">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-xs bg-accent-100 text-accent-800 px-2.5 py-1 rounded-full font-medium">{article.category}</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full">{tag}</span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading leading-tight mb-3">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views}阅读
                </span>
              </div>
            </div>

            <div className="aspect-[2/1] rounded-lg overflow-hidden mb-8 bg-background-200">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="article-content text-sm md:text-base"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 bg-background-100 -mx-4 md:-mx-6">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-lg font-semibold text-foreground-900 font-heading mb-5">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-background-200">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">{rel.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}